import React, { useState, useEffect, useRef } from "react";
import ToastPopup from "./ToastPopup";
import { CreateChecklistModalProps, createToastMessage, ToastMessage } from "../Interfaces/Interface";
import { apiCall } from "../main/Apicall";
const CreateChecklistModal: React.FC<CreateChecklistModalProps> = ({ isOpen, onClose, newChecklistName, setNewChecklistName, newChecklistDescription, setNewChecklistDescription, onSaveNewChecklist }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isIntegratedChecklist, setIsIntegratedChecklist] = useState<string>("Select");
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [selectedScope, setSelectedScope] = useState<string>("");
  const [controlOptions, setControlOptions] = useState<{ value: string; description: string }[]>([]);
  const [loadingControls, setLoadingControls] = useState<boolean>(false);
  const [prefix, setPrefix] = useState<string>("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const extractPrefixFromValue = (scopeValue: string): string => {
    let cleanValue = scopeValue.replace(/\s*Controls\s*/i, "");
    const prefix = cleanValue.trim().split(/\s+/)[0];
    return `${prefix}_`;
  };
  useEffect(() => {
    const fetchControlTypes = async () => {
      setLoadingControls(true);
      try {
        const response = await apiCall("get", "/api/checklist/templates/controlEnums");
        setControlOptions(response);
      } catch (error) {
        setMessage(createToastMessage("Failed to load control types", "error", 3000));
      } finally {
        setLoadingControls(false);
      }
    };
    if (isOpen) {
      fetchControlTypes();
      setIsIntegratedChecklist("Select");
      setSelectedControls([]);
      setSelectedScope("");
      setPrefix("");
      setNewChecklistName("");
    }
  }, [isOpen, setNewChecklistName]);
  const handleChecklistTypeChange = (value: string) => {
    setIsIntegratedChecklist(value);
    if (value === "Yes") {
      setSelectedScope("");
      setPrefix("");
      setNewChecklistName("");
    } else if (value === "No") {
      setSelectedControls([]);
      setPrefix("");
      setNewChecklistName("");
    } else {
      setSelectedControls([]);
      setSelectedScope("");
      setPrefix("");
      setNewChecklistName("");
    }
  };
  const handleCheckboxChange = (controlValue: string) => {
    setSelectedControls((prev) =>
      prev.includes(controlValue) ? prev.filter((c) => c !== controlValue) : [...prev, controlValue]
    );
  };
  const handleScopeChange = (scopeValue: string) => {
    setSelectedScope(scopeValue);
    const newPrefix = extractPrefixFromValue(scopeValue);
    setPrefix(newPrefix);
    setNewChecklistName(newPrefix);
  };
  const handleNameChange = (value: string) => {
    if (isIntegratedChecklist === "Select") {
      setMessage(createToastMessage("Please select Integrated Checklist option first", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Yes") {
      setNewChecklistName(value);
      return;
    }
    if (isIntegratedChecklist === "No") {
      if (!selectedScope) {
        setMessage(createToastMessage("Please select one scope first", "warning", 3000));
        return;
      }
      if (value === "") {
        setNewChecklistName(prefix);
      } else if (value.startsWith(prefix)) {
        setNewChecklistName(value);
      } else {
        const suffix = value.replace(prefix, "").replace(/^[^_]*_?/, "");
        setNewChecklistName(prefix + suffix);
        setMessage(createToastMessage("Scope prefix cannot be removed or modified", "warning", 2000));
      }
    }
  };
  const handleSaveWithValidation = () => {
    if (!newChecklistName.trim() || !newChecklistDescription.trim()) {
      setMessage(createToastMessage("Please fill all fields", "warning", 3000));
      return;
    }
    if (newChecklistName.length > 60) {
      setMessage(createToastMessage("Checklist Name cannot exceed 60 characters", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Select") {
      setMessage(createToastMessage("Please select Integrated Checklist option", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Yes" && selectedControls.length < 2) {
      setMessage(createToastMessage("Please select at least two controls for an integrated checklist", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "No") {
      if (!selectedScope) {
        setMessage(createToastMessage("Please select one scope for a normal checklist", "warning", 3000));
        return;
      }
      if (!newChecklistName.startsWith(prefix)) {
        setMessage(createToastMessage("Checklist name must start with the scope prefix", "warning", 3000));
        return;
      }
      if (newChecklistName.length <= prefix.length) {
        setMessage(createToastMessage("Please add a name after the prefix", "warning", 3000));
        return;
      }
    }
    const checklistData = { templateName: newChecklistName, description: newChecklistDescription, isIntegratedChecklist: isIntegratedChecklist === "Yes", controls: isIntegratedChecklist === "Yes" ? selectedControls : [], scope: isIntegratedChecklist === "No" ? selectedScope : "" };
    onSaveNewChecklist(checklistData);
    setNewChecklistName("");
    setNewChecklistDescription("");
    setIsIntegratedChecklist("Select");
    setSelectedControls([]);
    setSelectedScope("");
    setPrefix("");
    onClose();
  };
  const handleBeforeInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedScope) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      const inputEvent = e as any;
      if (
        inputEvent.inputType === "deleteContentBackward" ||
        inputEvent.inputType === "deleteContentForward" ||
        inputEvent.inputType === "deleteByCut"
      ) {
        if (cursorPosition <= prefix.length) {
          e.preventDefault();
          setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
        }
      }
    }
  };
  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedScope) {
      const input = e.currentTarget;
      if (input.selectionStart !== null && input.selectionStart < prefix.length) {
        input.setSelectionRange(prefix.length, input.selectionEnd ?? prefix.length);
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedScope) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if (
        (e.key === "Backspace" && cursorPosition <= prefix.length) ||
        (e.key === "Delete" && cursorPosition < prefix.length)
      ) {
        e.preventDefault();
        setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
      }
      if (e.key === "ArrowLeft" && cursorPosition === prefix.length) {
        e.preventDefault();
      }
      if (e.key === "Home") {
        e.preventDefault();
        input.setSelectionRange(prefix.length, prefix.length);
      }
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedScope) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < prefix.length) {
        e.preventDefault();
        setMessage(createToastMessage("Cannot paste into the prefix area", "warning", 2000));
      }
    }
  };
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Create Checklist</h3>
        <label>
          Integrated Checklist:
          <select className="mb-10" value={isIntegratedChecklist} onChange={(e) => handleChecklistTypeChange(e.target.value)}>
            <option value="Select">Select an option</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </label>
        <label>Checklist Name:
          <textarea ref={inputRef} value={newChecklistName} onChange={(e) => handleNameChange(e.target.value)} onKeyDown={handleKeyDown} onSelect={handleSelect} onBeforeInput={handleBeforeInput} onPaste={handlePaste} placeholder={isIntegratedChecklist === "No" ? selectedScope ? `Enter name after ${prefix}` : "Please select scope first" : "Enter checklist name"} disabled={isIntegratedChecklist === "No" && !selectedScope} />
        </label>
        <label>Description:
          <textarea value={newChecklistDescription} onChange={(e) => setNewChecklistDescription(e.target.value)} />
        </label>
        {isIntegratedChecklist === "Yes" && (
          <>
            <label className="controls-create-label">Select Controls (Minimum 2 required):</label>
            <div className="controls-create-section">
              {loadingControls ? (
                <div>Loading control types...</div>
              ) : (
                <div className="controls-create-list">
                  {controlOptions.map((control) => (
                    <label key={control.value} className="control-create-option">
                      <input type="checkbox" value={control.value} checked={selectedControls.includes(control.value)} onChange={() => handleCheckboxChange(control.value)} />{control.description} ({control.value})
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        {isIntegratedChecklist === "No" && (
          <>
            <label className="controls-create-label">Scope (Select any one):</label>
            <div className="controls-create-section">
              {loadingControls ? (
                <div>Loading control types...</div>
              ) : (
                <div className="controls-create-list">
                  {controlOptions.map((control) => (
                    <label key={control.value} className="control-create-option">
                      <input type="radio" name="normalChecklistScope" value={control.value} checked={selectedScope === control.value} onChange={() => handleScopeChange(control.value)} />{control.description} ({control.value})
                    </label>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        <div className="buttons">
          <button className="cancelbutton" onClick={onClose}>Cancel</button>
          <button className={`savebutton ${(isIntegratedChecklist === "Yes" && selectedControls.length < 2) || (isIntegratedChecklist === "No" && !selectedScope) ? "savebutton-disabled" : ""}`} onClick={handleSaveWithValidation} disabled={(isIntegratedChecklist === "Yes" && selectedControls.length < 2) || (isIntegratedChecklist === "No" && !selectedScope)}>Save</button>
        </div>
        {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      </div>
    </div>
  );
};
export default CreateChecklistModal;